import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { AuditProgressSteps } from './AuditProgressSteps'
import { useProcessQueue } from '@/hooks/useAmazonAudit'
import type { AuditJob } from '@/lib/api/amazon-listing'

interface AuditProgressScreenProps {
  job: AuditJob
}

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

export function AuditProgressScreen({ job }: AuditProgressScreenProps) {
  const { t } = useTranslation()
  useProcessQueue()

  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    if (!job.started_at) return
    const start = new Date(job.started_at).getTime()
    const tick = () => setElapsed(Math.floor((Date.now() - start) / 1000))
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [job.started_at])

  const p = job.progress
  const feStep = p.frontend_step ?? Math.min(p.current, 8)
  const feTotal = p.frontend_total ?? 8

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] p-8">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <Loader2 className="h-10 w-10 text-orange-500 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-bold text-neutral-900 dark:text-white">
            {t('amazonListing.progress.title')}
          </h2>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2">
            {t('amazonListing.progress.pleaseWait')}
          </p>
        </div>

        <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-6">
          <div className="flex justify-between items-center mb-4">
            <span className="text-sm font-medium text-neutral-600 dark:text-neutral-300">
              {t('amazonListing.progress.step', {
                current: feStep,
                total: feTotal,
              })}
            </span>
            <div className="flex items-center gap-3">
              {job.started_at && (
                <span className="text-xs text-neutral-400 font-mono">
                  {t('amazonListing.progress.elapsed')} {formatElapsed(elapsed)}
                </span>
              )}
              <span className="text-sm font-bold text-orange-500">
                {p.percentage}%
              </span>
            </div>
          </div>

          <div className="w-full bg-neutral-100 dark:bg-neutral-700 rounded-full h-2 mb-6">
            <div
              className="bg-gradient-to-r from-orange-400 to-orange-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${p.percentage}%` }}
            />
          </div>

          <AuditProgressSteps
            frontendStep={feStep}
            subStep={p.sub_step ?? null}
            subCurrent={p.sub_current ?? 0}
            subTotal={p.sub_total ?? 0}
          />
        </div>
      </div>
    </div>
  )
}
